import { useCallback, useContext, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthHandlerContext } from '../../../App'
import { REFRESH_TOKEN as REFRESH_TOKEN_KEY } from '../../../assets/constants'
import ROUTES from '../../../assets/routes'
import { refreshAccessToken } from '../../../services/auth/tokenRefresh'
import { getUserProfile } from '../../../services/apis/user'
import { isAccessTokenValid } from '../../../util/authentication'
import {
	clearPinSetupPending,
	isPinEnabledFlag,
	isPinSetupPending,
	setPinSetupPending,
	setPinUnlockPending,
} from '../../../util/pinBrowserFlags'
import {
	getMsUntilLoginSessionExpiry,
	isLoginSessionWindowValid,
} from '../../../util/loginSessionWindow'

export type PostLoginPinGate = 'setup' | 'ready'

/** Sync PIN flag from `/user/me` after auth tokens are persisted. */
export async function preparePostLoginPinGate(): Promise<PostLoginPinGate> {
	const user = await getUserProfile()
	return applyPinSetupGateFromUser(user)
}

export function applyPinSetupGateFromUser(user: { isPinEnabled?: boolean }): PostLoginPinGate {
	if (user.isPinEnabled) {
		clearPinSetupPending()
		return 'ready'
	}
	setPinSetupPending()
	return 'setup'
}

/**
 * Enforces the daily 6am IST session window: PIN unlock when enabled, otherwise full logout.
 * Also redirects while compulsory PIN setup is pending.
 */
export default function PinSessionUnlockGate({ children }: { children: React.ReactNode }) {
	const navigate = useNavigate()
	const { onLoggedOut } = useContext(AuthHandlerContext)

	const navigatingRef = useRef(false)

	const evaluate = useCallback(async () => {
		const hasRefresh = !!localStorage.getItem(REFRESH_TOKEN_KEY)
		if (!hasRefresh) {
			return
		}

		if (isPinSetupPending()) {
			navigate(ROUTES.login, { replace: true })
			return
		}

		if (isLoginSessionWindowValid()) {
			return
		}

		if (navigatingRef.current) return
		const ok = await refreshAccessToken()
		if (!ok) {
			navigate(ROUTES.login, { replace: true })
			return
		}
		if (!isAccessTokenValid()) {
			navigate(ROUTES.login, { replace: true })
			return
		}

		const pinOn = isPinEnabledFlag()
		if (!pinOn) {
			onLoggedOut()
			return
		}

		navigatingRef.current = true
		try {
			setPinUnlockPending()
			navigate(ROUTES.login, { replace: true })
		} finally {
			navigatingRef.current = false
		}
	}, [navigate, onLoggedOut])

	useEffect(() => {
		void evaluate()
	}, [evaluate])

	useEffect(() => {
		const onVisibility = () => {
			if (document.visibilityState !== 'visible') return
			void evaluate()
		}
		document.addEventListener('visibilitychange', onVisibility)
		return () => document.removeEventListener('visibilitychange', onVisibility)
	}, [evaluate])

	useEffect(() => {
		if (!localStorage.getItem(REFRESH_TOKEN_KEY)) return

		const ms = getMsUntilLoginSessionExpiry()
		if (ms == null) {
			void evaluate()
			return
		}

		const timeoutId = setTimeout(() => {
			void evaluate()
		}, ms + 50)

		return () => clearTimeout(timeoutId)
	}, [evaluate])

	return <>{children}</>
}
